import React from 'react';
import { useDispatch } from 'react-redux';

import { BrStatus, PartnerStatus, PartnerType } from 'types/Partner';
import WarningDialog from 'components/Common/WarningDialog';
import { closeAllModals } from 'reducers/modalReducer';
import { useUpdateLegalPartnerStatus, useUpdatePrivateIndividualStatus } from 'hooks/queries/useUpdatePartner';
import { Formik } from 'formik';
import { Grid } from '@mui/material';
import ReasonFields from 'views/PartnerTabs/RejectWithdrawDialog/ReasonFields';
import { rejectWithdrawSchema } from 'views/PartnerTabs/RejectWithdrawDialog/validationSchema';

export enum RevokeReason {
  PARTNER_REVOKED_CONSENT = 'PARTNER_REVOKED_CONSENT',
  RM_WITHDREW_PARTNER = 'RM_WITHDREW_PARTNER',
  BANK_REJECTED_PARTNER = 'BANK_REJECTED_PARTNER',
}

export type RejectWithdrawFormType = {
  rejectWithdrawReason: string;
  rejectWithdrawReasonOthers: string;
};

/**
 * Modal for the Reject/Withdraw functions of the partner overview.
 * @param props.action The action to be executed when clicking OK
 * @param props.modalType The WarningDialogs type - "Reject" or "Withdraw"
 * @constructor
 */
const RejectWithdrawDialog = (props: { action: any; modalType: BrStatus | PartnerStatus; partner: any }) => {
  const dispatch = useDispatch();
  const updateCall =
    props.partner?.partnerType === PartnerType.INDIVIDUAL_PARTNER
      ? useUpdatePrivateIndividualStatus
      : useUpdateLegalPartnerStatus;
  const { mutate, isPending } = updateCall(props.partner, props.modalType);

  function getActionText(modalType: BrStatus | PartnerStatus) {
    if (modalType === PartnerStatus.REJECTED) {
      return 'reject';
    } else if (modalType === PartnerStatus.WITHDRAWN) {
      return 'withdraw';
    } else if (props.partner?.status === PartnerStatus.REJECTED || props.partner?.status === PartnerStatus.WITHDRAWN) {
      return 're-activate';
    }
  }

  const actionText = getActionText(props.modalType);

  const isRejectOrWithdrawType = [PartnerStatus.WITHDRAWN, PartnerStatus.REJECTED].includes(
    props.modalType as PartnerStatus,
  );

  return (
    <Formik
      initialValues={{
        rejectWithdrawReason: '',
        rejectWithdrawReasonOthers: '',
      }}
      validationSchema={rejectWithdrawSchema(props.modalType)}
      onSubmit={(values) => {
        // @ts-ignore
        mutate(values);
      }}
    >
      {({ submitForm }) => (
        <WarningDialog
          dialogOpen={true}
          bodyText={`Are you sure you want to ${actionText} this partner?\n You can't undo this step.`}
          title="Warning"
          affirmText={`Yes, ${actionText}`}
          action={submitForm}
          cancelAction={() => {
            dispatch(closeAllModals());
          }}
          isCancelNeeded={true}
          isLoading={isPending}
          disabled={isPending}
        >
          {isRejectOrWithdrawType && (
            <Grid container spacing={2}>
              <ReasonFields modalType={props.modalType as PartnerStatus.WITHDRAWN | PartnerStatus.REJECTED} />
            </Grid>
          )}
        </WarningDialog>
      )}
    </Formik>
  );
};

export default RejectWithdrawDialog;
